import React, { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "";
const HOURS_24 = Array.from({ length: 24 }, (_, index) => index);

const emptyForm = {
  cardId: "",
  oneWaySlots: [{ hour: 7, price: "" }],
  hourPackages: [{ hours: "", price: "" }],
  additionalHourPrice: "",
};

const padHour = (hour) => `${String(hour).padStart(2, "0")}:00`;

const toNonNegative = (value) => {
  if (value === "" || value === null || value === undefined) {
    return "";
  }

  const number = Number(value);

  if (!Number.isFinite(number) || number < 0) {
    return 0;
  }

  return number;
};

const SpeedBoatRates = () => {
  const [boats, setBoats] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");

  const selectedBoat = useMemo(
    () => boats.find((boat) => String(boat.cardId) === String(form.cardId)),
    [boats, form.cardId]
  );

  const isOneWay = selectedBoat?.inMumbai === true;
  const isTwoWay = selectedBoat?.inGoa === true;

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  });

  const loadAll = async () => {
    const [boatsResponse, ratesResponse] = await Promise.all([
      fetch(`${API_BASE_URL}/api/yacht-details/boats`),
      fetch(`${API_BASE_URL}/api/speedboat-rates`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      }),
    ]);

    const boatsResult = boatsResponse.ok ? await boatsResponse.json() : [];
    const ratesResult = ratesResponse.ok ? await ratesResponse.json() : [];

    setBoats(
      (Array.isArray(boatsResult) ? boatsResult : []).filter(
        (boat) => boat.pageKey === "SpeedBoat"
      )
    );
    setRows(Array.isArray(ratesResult) ? ratesResult : []);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await loadAll();
      } catch (error) {
        console.error(error);
        alert("Failed to load speed boat times");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const updateSlot = (index, field, value) => {
    setForm((previous) => {
      const next = [...previous.oneWaySlots];
      next[index] = { ...next[index], [field]: value };
      return { ...previous, oneWaySlots: next };
    });
  };

  const updatePackage = (index, field, value) => {
    setForm((previous) => {
      const next = [...previous.hourPackages];
      next[index] = { ...next[index], [field]: value };
      return { ...previous, hourPackages: next };
    });
  };

  const handleSave = async () => {
    if (!form.cardId) {
      alert("Please select a speed boat");
      return;
    }

    if (!isOneWay && !isTwoWay) {
      alert("This boat has no One Way or Two Way selection. Edit it in Speed Boats first.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        cardId: form.cardId,
        boatTitle: selectedBoat?.title || selectedBoat?.label || "",
        oneWaySlots: isOneWay
          ? form.oneWaySlots.filter(
              (item) => item.hour !== "" && item.price !== "" && Number(item.price) >= 0
            )
          : [],
        hourPackages: isTwoWay
          ? form.hourPackages.filter(
              (item) =>
                Number(item.hours) > 0 &&
                item.price !== "" &&
                Number(item.price) >= 0
            )
          : [],
      };

      if (
        isTwoWay &&
        form.additionalHourPrice !== "" &&
        form.additionalHourPrice !== null
      ) {
        payload.additionalHourPrice = Math.max(
          0,
          Number(form.additionalHourPrice) || 0
        );
      }

      const response = await fetch(`${API_BASE_URL}/api/speedboat-rates`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to save");
      }

      await loadAll();
      setEditingId(null);
      setForm({
        ...emptyForm,
        cardId: form.cardId,
      });
      alert(result.message || "Saved");
    } catch (error) {
      alert(error.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row._id);
    setForm({
      cardId: String(row.cardId),
      oneWaySlots:
        row.oneWaySlots?.length > 0
          ? row.oneWaySlots.map((item) => ({
              hour: item.hour,
              price: item.price,
            }))
          : [{ hour: 7, price: "" }],
      hourPackages:
        row.hourPackages?.length > 0
          ? row.hourPackages.map((item) => ({
              hours: item.hours,
              price: item.price,
            }))
          : [{ hours: "", price: "" }],
      additionalHourPrice:
        row.additionalHourPrice > 0 ? row.additionalHourPrice : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteItem = async (row, kind, value) => {
    const label =
      kind === "one-way"
        ? `${padHour(value)} one way time`
        : kind === "package"
          ? `${value} hour package`
          : "additional hour price";

    if (!window.confirm(`Delete ${label} for "${row.boatTitle}"?`)) {
      return;
    }

    try {
      setSaving(true);
      const body =
        kind === "one-way"
          ? { removeOneWayHour: value }
          : kind === "package"
            ? { removePackageHours: value }
            : { clearAdditionalHourPrice: true };

      const response = await fetch(
        `${API_BASE_URL}/api/speedboat-rates/${row._id}`,
        {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify(body),
        }
      );
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete");
      }

      await loadAll();
    } catch (error) {
      alert(error.message || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete all times and prices for "${row.boatTitle}"?`)) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(
        `${API_BASE_URL}/api/speedboat-rates/${row._id}`,
        {
          method: "DELETE",
          headers: authHeaders(),
        }
      );
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete");
      }

      await loadAll();
      if (editingId === row._id) {
        resetForm();
      }
    } catch (error) {
      alert(error.message || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Speed Boat Time Slots</h1>
          <p>
            New times are added to what is already saved. Use delete in the
            table below to remove a time or package.
          </p>
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{editingId ? "Update Times & Prices" : "Set Times & Prices"}</h2>
          <p>
            One Way / Two Way comes from the Speed Boats page. Create boats
            there first.
          </p>
        </div>

        <div className="cms-form-grid cms-form-grid-4">
          <div className="cms-form-group">
            <label>Speed Boat</label>
            <select
              value={form.cardId}
              onChange={(e) =>
                setForm({
                  ...emptyForm,
                  cardId: e.target.value,
                })
              }
            >
              <option value="">Select boat</option>
              {boats.map((boat) => (
                <option key={String(boat.cardId)} value={String(boat.cardId)}>
                  {boat.title || boat.label}
                </option>
              ))}
            </select>
          </div>

          {selectedBoat ? (
            <div className="cms-form-group cms-form-compact">
              <label>Trip type on this boat</label>
              <p className="cms-inquiries-empty" style={{ margin: 0 }}>
                {[
                  isOneWay ? "One Way" : "",
                  isTwoWay ? "Two Way" : "",
                ]
                  .filter(Boolean)
                  .join(" • ") || "Not set"}
              </p>
            </div>
          ) : null}
        </div>

        {selectedBoat && isOneWay ? (
          <div style={{ marginTop: 18 }}>
            <h3 className="cms-section-card-header" style={{ padding: 0 }}>
              One Way departure hours
            </h3>
            <p className="cms-inquiries-empty">
              24-hour times such as 7, 8, 9 with a price for each.
            </p>
            {form.oneWaySlots.map((slot, index) => (
              <div className="cms-form-grid cms-form-grid-4" key={`ow-${index}`}>
                <div className="cms-form-group">
                  <label>Hour</label>
                  <select
                    value={slot.hour}
                    onChange={(e) =>
                      updateSlot(index, "hour", Number(e.target.value))
                    }
                  >
                    {HOURS_24.map((hour) => (
                      <option key={hour} value={hour}>
                        {padHour(hour)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cms-form-group">
                  <label>Price (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={slot.price}
                    onChange={(e) =>
                      updateSlot(index, "price", toNonNegative(e.target.value))
                    }
                  />
                </div>
                <div className="cms-form-group">
                  <label>&nbsp;</label>
                  <button
                    type="button"
                    className="cms-delete-btn"
                    onClick={() =>
                      setForm((previous) => ({
                        ...previous,
                        oneWaySlots: previous.oneWaySlots.filter(
                          (_, itemIndex) => itemIndex !== index
                        ),
                      }))
                    }
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="cms-upload-btn"
              onClick={() =>
                setForm((previous) => ({
                  ...previous,
                  oneWaySlots: [
                    ...previous.oneWaySlots,
                    { hour: 7, price: "" },
                  ],
                }))
              }
            >
              Add departure hour
            </button>
          </div>
        ) : null}

        {selectedBoat && isTwoWay ? (
          <div style={{ marginTop: 24 }}>
            <h3 className="cms-section-card-header" style={{ padding: 0 }}>
              Two Way hour packages
            </h3>
            <p className="cms-inquiries-empty">
              Guests pick from and to times. Shorter trips are priced from the
              hour package (for example 3 hours at ₹1,500 means 1 hour is ₹500).
            </p>
            {form.hourPackages.map((item, index) => (
              <div className="cms-form-grid cms-form-grid-4" key={`pkg-${index}`}>
                <div className="cms-form-group">
                  <label>Hours included</label>
                  <input
                    type="number"
                    min="0"
                    value={item.hours}
                    onChange={(e) =>
                      updatePackage(index, "hours", toNonNegative(e.target.value))
                    }
                  />
                </div>
                <div className="cms-form-group">
                  <label>Price (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={item.price}
                    onChange={(e) =>
                      updatePackage(index, "price", toNonNegative(e.target.value))
                    }
                  />
                </div>
                <div className="cms-form-group">
                  <label>&nbsp;</label>
                  <button
                    type="button"
                    className="cms-delete-btn"
                    onClick={() =>
                      setForm((previous) => ({
                        ...previous,
                        hourPackages: previous.hourPackages.filter(
                          (_, itemIndex) => itemIndex !== index
                        ),
                      }))
                    }
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="cms-upload-btn"
              onClick={() =>
                setForm((previous) => ({
                  ...previous,
                  hourPackages: [
                    ...previous.hourPackages,
                    {
                      hours: "",
                      price: "",
                    },
                  ],
                }))
              }
            >
              Add hour package
            </button>

            <div className="cms-form-grid cms-form-grid-4" style={{ marginTop: 16 }}>
              <div className="cms-form-group">
                <label>Additional hour price (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={form.additionalHourPrice}
                  onChange={(e) =>
                    setForm((previous) => ({
                      ...previous,
                      additionalHourPrice: toNonNegative(e.target.value),
                    }))
                  }
                  placeholder="Optional"
                />
              </div>
              {form.additionalHourPrice !== "" ? (
                <div className="cms-form-group">
                  <label>&nbsp;</label>
                  <button
                    type="button"
                    className="cms-delete-btn"
                    onClick={() =>
                      setForm((previous) => ({
                        ...previous,
                        additionalHourPrice: "",
                      }))
                    }
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="cms-bottom-actions">
          <button
            type="button"
            className="cms-save-btn"
            onClick={handleSave}
            disabled={saving || !form.cardId}
          >
            {saving ? "Saving..." : editingId ? "Save Changes" : "Save"}
          </button>
          {editingId ? (
            <button type="button" className="cms-upload-btn" onClick={resetForm}>
              Cancel Update
            </button>
          ) : null}
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>Saved Speed Boat Prices</h2>
        </div>

        {rows.length === 0 ? (
          <p className="cms-inquiries-empty">No speed boat prices yet.</p>
        ) : (
          <div className="cms-enquiries-table-wrap">
            <table className="cms-enquiries-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Boat</th>
                  <th>One Way times</th>
                  <th>Two Way packages</th>
                  <th>Extra hour</th>
                  <th>Delete or update whole row</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row._id}>
                    <td>{index + 1}</td>
                    <td>{row.boatTitle || "—"}</td>
                    <td>
                      {(row.oneWaySlots || []).length === 0 ? (
                        "—"
                      ) : (
                        <div>
                          {(row.oneWaySlots || []).map((item) => (
                            <div
                              key={`${row._id}-ow-${item.hour}`}
                              className="cms-item-row-actions"
                              style={{ marginBottom: 6 }}
                            >
                              <span>
                                {padHour(item.hour)} ₹
                                {Number(item.price).toLocaleString("en-IN")}
                              </span>
                              <button
                                type="button"
                                className="cms-delete-btn"
                                onClick={() =>
                                  handleDeleteItem(row, "one-way", item.hour)
                                }
                                disabled={saving}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {(row.hourPackages || []).length === 0 ? (
                        "—"
                      ) : (
                        <div>
                          {(row.hourPackages || []).map((item) => (
                            <div
                              key={`${row._id}-pkg-${item.hours}`}
                              className="cms-item-row-actions"
                              style={{ marginBottom: 6 }}
                            >
                              <span>
                                {item.hours}h ₹
                                {Number(item.price).toLocaleString("en-IN")}
                              </span>
                              <button
                                type="button"
                                className="cms-delete-btn"
                                onClick={() =>
                                  handleDeleteItem(row, "package", item.hours)
                                }
                                disabled={saving}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {row.additionalHourPrice ? (
                        <div className="cms-item-row-actions">
                          <span>
                            ₹{" "}
                            {Number(row.additionalHourPrice).toLocaleString(
                              "en-IN"
                            )}
                          </span>
                          <button
                            type="button"
                            className="cms-delete-btn"
                            onClick={() =>
                              handleDeleteItem(row, "extra")
                            }
                            disabled={saving}
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <div className="cms-item-row-actions">
                        <button
                          type="button"
                          className="cms-upload-btn"
                          onClick={() => startEdit(row)}
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="cms-delete-btn"
                          onClick={() => handleDelete(row)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeedBoatRates;
